<script setup>
/**
 * Lead capture's chooser: which customer details the assistant may save to a customer's
 * profile, and which labels it may put on the CUSTOMER (not the chat).
 *
 * The same states as `AllowlistEditor.vue`, for the same reasons, and the same structural
 * guarantee: while Chatwoot cannot be read this renders what is saved as text, and the
 * card leaves `chosen` out of its patch, so nothing here can wipe a saved choice.
 *
 * WHAT IT ADDS: a detail is saved as a SNAPSHOT — `{ key, label, type, values?,
 * description? }` — because the sidecar builds the assistant's tool from it without asking
 * Chatwoot on every message. Ticking copies what Chatwoot has now; a saved detail Chatwoot
 * no longer has stays listed and ticked, exactly like an orphaned team.
 *
 * VOCABULARY: Chatwoot calls these "custom attributes". The owner reads "details" on the
 * customer's "profile" — every string comes from `CONTACT_DETAILS` in `i18n.js`.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import { useWasloI18n } from '../i18n.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';

const props = defineProps({
  /** `'loading' | 'ready' | 'unreadable'`. */
  state: {
    type: String,
    default: 'loading',
    validator: value => ['loading', 'ready', 'unreadable'].includes(value),
  },
  /** `{ contactFields, labels, … }` from Chatwoot, or null while it is not `ready`. */
  targets: { type: Object, default: null },
  /** What is picked right now: `{ contactFields, contactLabels, … }`. */
  chosen: { type: Object, required: true },
});

const emit = defineEmits(['update:chosen', 'retry']);

const { t } = useWasloI18n();
const accountId = useWasloAccountId();

const key = name => `WASLO.PERSONALITY.PERMISSIONS.CONTACT_DETAILS.${name}`;

/** Chatwoot's own settings page for new details (`attributes.routes.js`). */
const settingsRoute = computed(() => ({
  name: 'attributes_list',
  params: { accountId: accountId.value },
}));

const snapshot = field => ({
  key: field.key,
  label: field.label,
  type: field.type,
  ...(field.values ? { values: [...field.values] } : {}),
  ...(field.description ? { description: field.description } : {}),
});

/** The three built-in fields are ours to name, in the owner's language. */
const fieldName = field =>
  field.type === 'standard'
    ? t(key(`STANDARD.${field.key.toUpperCase()}`))
    : field.label;

const typeName = field => t(key(`TYPES.${field.type.toUpperCase()}`));

const fieldRows = computed(() => {
  if (props.state !== 'ready' || !props.targets) return [];
  const known = props.targets.contactFields;
  const seen = new Set(known.map(field => field.key));
  return [
    ...known,
    ...props.chosen.contactFields.filter(field => !seen.has(field.key)),
  ];
});

const labelRows = computed(() => {
  if (props.state !== 'ready' || !props.targets) return [];
  const known = props.targets.labels;
  const seen = new Set(known);
  return [
    ...known,
    ...props.chosen.contactLabels.filter(label => !seen.has(label)),
  ];
});

const isFieldPicked = field =>
  props.chosen.contactFields.some(entry => entry.key === field.key);

const toggleField = (field, next) => {
  const without = props.chosen.contactFields.filter(
    entry => entry.key !== field.key
  );
  emit('update:chosen', {
    ...props.chosen,
    contactFields: next ? [...without, snapshot(field)] : without,
  });
};

const toggleLabel = (label, next) => {
  const without = props.chosen.contactLabels.filter(entry => entry !== label);
  emit('update:chosen', {
    ...props.chosen,
    contactLabels: next ? [...without, label] : without,
  });
};

const chosenCount = computed(
  () => props.chosen.contactFields.length + props.chosen.contactLabels.length
);

/** What is saved, as plain text, for the state where nothing can be ticked. */
const keptNames = computed(() =>
  [
    ...props.chosen.contactFields.map(fieldName),
    ...props.chosen.contactLabels,
  ].join(t('WASLO.COMMON.LIST_SEPARATOR'))
);
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-contact-choices>
    <div class="flex flex-col gap-1">
      <h4 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('FIELDS_HEADING')) }}
      </h4>
      <p class="mb-0 text-sm leading-6 max-w-[58ch] text-n-slate-11">
        {{ t(key('FIELDS_DESCRIPTION')) }}
      </p>
      <!-- The one outcome she cannot guess from the tick list: a detail that is already
           another customer's is never overwritten. Said here, not in an error later. -->
      <p
        class="mb-0 text-sm leading-6 max-w-[58ch] text-n-slate-11"
        data-waslo-contact-conflict
      >
        {{ t(key('FIELDS_CONFLICT')) }}
      </p>
    </div>

    <div
      v-if="state === 'loading'"
      class="flex items-center gap-2 text-sm text-n-slate-11"
      data-waslo-choices-state="loading"
    >
      <Spinner class="size-4" />
      {{ t(key('LOADING')) }}
    </div>

    <!-- Unreadable: NOT an empty tick list — see `AllowlistEditor.vue`, state 5. -->
    <div
      v-else-if="state === 'unreadable'"
      class="flex flex-col items-start gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-amber-8 rounded-xl bg-n-solid-1"
      data-waslo-choices-state="unreadable"
    >
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t(key('UNREADABLE')) }}
      </p>
      <p
        v-if="chosenCount > 0"
        class="mb-0 text-sm font-medium text-n-slate-12"
        dir="auto"
        data-waslo-choices-kept
      >
        {{ t(key('UNREADABLE_KEPT'), { names: keptNames }) }}
      </p>
      <Button
        :label="t(key('UNREADABLE_RETRY'))"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-choices-retry
        @click="emit('retry')"
      />
    </div>

    <template v-else>
      <div class="flex flex-col gap-2" data-waslo-choices-state="ready">
        <label
          v-for="field in fieldRows"
          :key="`field:${field.key}`"
          class="flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer bg-n-alpha-1 hover:bg-n-alpha-2"
          :data-waslo-choice="`field:${field.key}`"
        >
          <Checkbox
            class="mt-1"
            :model-value="isFieldPicked(field)"
            @update:model-value="value => toggleField(field, value)"
          />
          <span class="flex flex-col min-w-0 gap-0.5">
            <span class="flex flex-wrap items-center gap-2">
              <!-- The account's own detail name — an isolate, so an Arabic name cannot
                   exchange punctuation with the chip beside it. -->
              <span class="text-sm break-words text-n-slate-12">
                <bdi>{{ fieldName(field) }}</bdi>
              </span>
              <span
                class="px-1.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
                data-waslo-choice-type
              >
                {{ typeName(field) }}
              </span>
            </span>
            <span
              v-if="field.description"
              class="text-xs break-words text-n-slate-10"
              dir="auto"
              data-waslo-choice-description
            >
              {{ field.description }}
            </span>
          </span>
        </label>
        <router-link
          :to="settingsRoute"
          class="text-sm font-medium text-n-blue-11 hover:underline"
          data-waslo-contact-settings-link
        >
          {{ t(key('ADD_DETAIL')) }}
        </router-link>
      </div>

      <div class="flex flex-col gap-1">
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t(key('LABELS_HEADING')) }}
        </h4>
        <p class="mb-0 text-sm leading-6 max-w-[58ch] text-n-slate-11">
          {{ t(key('LABELS_DESCRIPTION')) }}
        </p>
      </div>

      <p
        v-if="labelRows.length === 0"
        class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
        data-waslo-choices-state="none"
      >
        {{ t(key('NONE_LABELS')) }}
      </p>
      <div v-else class="flex flex-col gap-2" data-waslo-contact-labels>
        <label
          v-for="label in labelRows"
          :key="`label:${label}`"
          class="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer bg-n-alpha-1 hover:bg-n-alpha-2"
          :data-waslo-choice="`label:${label}`"
        >
          <Checkbox
            :model-value="chosen.contactLabels.includes(label)"
            @update:model-value="value => toggleLabel(label, value)"
          />
          <span class="min-w-0 text-sm break-words text-n-slate-12">
            <bdi>{{ label }}</bdi>
          </span>
        </label>
      </div>

      <p class="mb-0 text-xs text-n-slate-10" data-waslo-choices-count>
        {{ t(key('CHOSEN'), chosenCount) }}
      </p>
    </template>
  </div>
</template>
